import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";
import TryItButton from "../../../TryItButton";
import OutputBox from "../../../OutputBox";

export default function JS_Ternary() {
  return (
    <div className="tutorial-container read-only">
      <h1 className="tutorial-title">⚡ JavaScript Ternary Operator (?:)</h1>

      <p className="tutorial-intro">
        The <code className="highlight_code">ternary operator (?:)</code> is a
        concise one-line version of an if...else statement. It’s great for quick
        inline checks, such as user messages or access logic at Edu-Tech Master
        Academy.
      </p>

      <div
        className="Key_point_container"
        style={{ background: "linear-gradient(135deg, #ede9fe, #ddd6fe)" }}
      >
        <h2 style={{ color: "#5b21b6" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>Short form of if...else.</li>
          <li>Useful inside assignments, JSX, or inline displays.</li>
          <li>Improves code readability for quick checks.</li>
        </ul>
      </div>
      {/* 🔹 Explanation */}
      <div className="tutorial-section">
        <h2 className="section-title">💡 What is the Ternary Operator?</h2>
        <p>
          The <code className="highlight_code">ternary operator</code> is a
          compact one-line version of <code>if...else</code>. It evaluates a
          condition and returns a value based on whether the condition is{" "}
          <b>true</b> or <b>false</b>.
        </p>
        <p>
          Syntax:{" "}
          <code className="highlight_code">
            condition ? value_if_true : value_if_false
          </code>
        </p>
        <p>
          Use it for short conditional expressions, quick decisions, or inline
          assignments. At <b>Edu-Tech Master Academy</b>, it's ideal for simple
          checks in variables, console messages, or JSX rendering.
        </p>
      </div>
      {/* Example 1 */}
      <div className="tutorial-section">
        <h2 className="section-title">1️⃣ Age Check Example</h2>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`let age = 20;
let message = age >= 18 ? "Welcome to Edu-Tech Master Academy!" : "You must be 18+ to join.";
console.log(message);`}
          />
          <TryItButton
            code={`let age = 20;
let message = age >= 18 ? "Welcome to Edu-Tech Master Academy!" : "You must be 18+ to join.";
console.log(message);`}
          />
        </div>
        <OutputBox>
          <pre>Welcome to Edu-Tech Master Academy!</pre>
        </OutputBox>
      </div>

      {/* Example 2 */}
      <div className="tutorial-section">
        <h2 className="section-title">2️⃣ Course Access Example</h2>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`let isPremium = false;
let access = isPremium ? "Access granted to all courses." : "Upgrade to Premium for full access.";
console.log(access);`}
          />
          <TryItButton
            code={`let isPremium = false;
let access = isPremium ? "Access granted to all courses." : "Upgrade to Premium for full access.";
console.log(access);`}
          />
        </div>
        <OutputBox>
          <pre>Upgrade to Premium for full access.</pre>
        </OutputBox>
      </div>

      {/* Example 3 */}
      <div className="tutorial-section">
        <h2 className="section-title">3️⃣ Exam Result Example</h2>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`let marks = 75;
let result = marks >= 50 ? "Pass - Well done!" : "Fail - Try again at Edu-Tech Master Academy.";
console.log(result);`}
          />
          <TryItButton
            code={`let marks = 75;
let result = marks >= 50 ? "Pass - Well done!" : "Fail - Try again at Edu-Tech Master Academy.";
console.log(result);`}
          />
        </div>
        <OutputBox>
          <pre>Pass - Well done!</pre>
        </OutputBox>
      </div>

      {/* Example 4 */}
      <div className="tutorial-section">
        <h2 className="section-title">4️⃣ Quick Login Message</h2>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`let loggedIn = true;
console.log(loggedIn ? "Dashboard loaded for Edu-Tech Master Academy." : "Please log in first.");`}
          />
          <TryItButton
            code={`let loggedIn = true;
console.log(loggedIn ? "Dashboard loaded for Edu-Tech Master Academy." : "Please log in first.");`}
          />
        </div>
        <OutputBox>
          <pre>Dashboard loaded for Edu-Tech Master Academy.</pre>
        </OutputBox>
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #ddd6fe, #c4b5fd)",
          padding: "1rem",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#5b21b6" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>Use ternary operators for quick inline decisions.</li>
          <li>Combine ternary with JSX in React components.</li>
          <li>
            Try nested ternary for multi-level decisions (careful with
            readability).
          </li>
          <li>Compare with if...else statements for complex logic.</li>
        </ul>
      </div>
    </div>
  );
}
